import { useEffect, useId, useRef, type ReactNode } from 'react'
import styles from './Dialog.module.css'

interface DialogProps {
  open: boolean
  title: string
  onClose: () => void
  onSubmit?: () => void
  children: ReactNode
  footer?: ReactNode
  /** Rendered between the header and the scrolling body, e.g. a tablist. */
  toolbar?: ReactNode
  /** Controls shown in the header, beside the title, e.g. a version selector. */
  headerActions?: ReactNode
  wide?: boolean
  xwide?: boolean
  /** Element to focus once the dialog opens; defaults to the first focusable control. */
  initialFocusSelector?: string
}

/**
 * Accessible modal built on the native `<dialog>` element, so focus trapping, the
 * backdrop and Escape come from the platform. Focus is explicitly returned to the
 * element that opened the dialog.
 */
export function Dialog({
  open,
  title,
  onClose,
  onSubmit,
  children,
  footer,
  toolbar,
  headerActions,
  wide,
  xwide,
  initialFocusSelector,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const opener = useRef<HTMLElement | null>(null)
  const titleId = useId()

  /**
   * Restore focus on the next frame. Closing a modal removes it from the top layer,
   * and focusing synchronously before that happens leaves focus on the body instead
   * of the control that opened the dialog.
   */
  const restoreFocus = () => {
    const target = opener.current
    opener.current = null
    if (!target) return
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => target.focus())
    else target.focus()
  }

  useEffect(() => {
    const element = ref.current
    if (!element) return

    if (open) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      if (!element.open) {
        if (typeof element.showModal === 'function') element.showModal()
        else element.setAttribute('open', '')
      }
      const target = initialFocusSelector
        ? element.querySelector<HTMLElement>(initialFocusSelector)
        : element.querySelector<HTMLElement>('[data-autofocus]')
      target?.focus()
      return
    }

    if (element.open) {
      if (typeof element.close === 'function') element.close()
      else element.removeAttribute('open')
      restoreFocus()
    }
  }, [open, initialFocusSelector])

  // Note: focus return happens on the `open: true -> false` transition above. Doing it
  // from an unmount cleanup is unsound under StrictMode, which simulates an unmount
  // immediately after mount and would clear the captured opener straight away. Callers
  // therefore keep this component mounted and toggle `open`.

  return (
    <dialog
      ref={ref}
      className={`${styles.dialog} ${wide ? styles.wide : ''} ${xwide ? styles.xwide : ''}`}
      aria-labelledby={titleId}
      onCancel={(event) => {
        // Escape must discard rather than submit.
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose()
      }}
    >
      <form
        method="dialog"
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit?.()
        }}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close dialog">
            &#10005;
          </button>
        </div>
        {toolbar}
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </form>
    </dialog>
  )
}
